import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, ReferenceLine, ResponsiveContainer, Tooltip,
} from "recharts";
import { Gauge, CheckCircle2, XCircle, CircleDashed, History, Sigma } from "lucide-react";
import { HAZARD_LABEL } from "@/lib/hazard";

const skillTone = (b) =>
  b == null ? "text-atlas-secondary/80" : b >= 0.1 ? "text-emerald-400" : b >= -0.05 ? "text-atlas-cyan" : "text-amber-300";

const skillLabel = (b) =>
  b == null ? "no data yet" : b >= 0.1 ? "skilled vs climatology" : b >= -0.05 ? "well-calibrated (≈ climatology)" : "collecting data — small sample";

// Per-hazard Brier Skill Score + a compact reliability diagram (predicted vs observed).
export const AccuracyBlock = ({ report, hazard, testId }) => {
  if (!report) return null;
  const hz = (report.hazards || []).find((h) => h.hazard === hazard);
  const overall = report.overall || {};
  const stat = hz && hz.resolved > 0 ? hz : null;
  const bss = stat ? stat.brier_skill : overall.brier_skill;
  const brier = stat ? stat.brier : overall.brier;
  const auc = stat ? stat.auc : overall.auc;
  const resolved = stat ? stat.resolved : overall.resolved;
  const scope = stat ? HAZARD_LABEL[hazard] || hazard : "all hazards";
  const reliable = stat ? stat.reliable : overall.reliable;

  const pts = (hz?.points || [])
    .filter((p) => p.observed != null && p.n > 0)
    .map((p) => ({ x: p.predicted, y: p.observed, n: p.n }));
  // Fall back to the aggregate reliability curve when this hazard has no resolved outcomes yet.
  const aggPts = pts.length
    ? pts
    : (report.hazards || [])
        .flatMap((h) => h.points || [])
        .filter((p) => p.observed != null && p.n > 0)
        .map((p) => ({ x: p.predicted, y: p.observed, n: p.n }));
  const diagScope = pts.length ? scope : "all hazards";

  return (
    <div className="mt-2 border border-white/10 bg-white/[0.02] p-2.5" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Gauge className="w-3.5 h-3.5 text-atlas-cyan" />
        <span className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono">
          Forecast Accuracy · {scope}
        </span>
      </div>

      <div className="flex items-stretch gap-3">
        <div className="shrink-0 w-[92px]">
          <div className={`text-2xl font-mono leading-none ${skillTone(bss)}`} title="Brier Skill Score vs climatology: >0 beats always guessing the base rate">
            {bss == null ? "—" : (bss > 0 ? "+" : "") + bss.toFixed(2)}
          </div>
          <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/75 mb-1.5">Brier skill</div>
          <div className={`text-[8px] font-mono leading-tight mb-1 ${skillTone(bss)}`} data-testid="accuracy-skill-label">{bss != null && !reliable ? `small sample (${resolved}/20 scored)` : skillLabel(bss)}</div>
          <div className="text-[9px] font-mono text-atlas-secondary/85">
            Brier {brier == null ? "—" : brier.toFixed(3)}
          </div>
          <div className="text-[9px] font-mono text-atlas-secondary/85">
            AUC {auc == null ? "—" : auc.toFixed(2)}
          </div>
          <div className="text-[9px] font-mono text-atlas-secondary/75">{resolved || 0} resolved</div>
        </div>

        <div className="flex-1 min-w-0">
          {aggPts.length > 0 ? (
            <div className="h-36 w-full border border-white/10 bg-atlas-void/60">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 112 }}>
                <ScatterChart margin={{ top: 6, right: 8, bottom: 2, left: -22 }}>
                  <XAxis type="number" dataKey="x" domain={[0, 1]} tick={{ fill: "#8B98A5", fontSize: 7 }} ticks={[0, 0.5, 1]} />
                  <YAxis type="number" dataKey="y" domain={[0, 1]} tick={{ fill: "#8B98A5", fontSize: 7 }} ticks={[0, 0.5, 1]} width={30} />
                  <ZAxis type="number" dataKey="n" range={[24, 120]} />
                  <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]} stroke="#8B98A5" strokeDasharray="3 3" />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    contentStyle={{ background: "#0D1117", border: "1px solid rgba(255,255,255,0.15)", fontSize: 9, fontFamily: "monospace" }}
                    formatter={(v, k) => [typeof v === "number" ? v.toFixed(2) : v, k === "x" ? "predicted" : k === "y" ? "observed" : k]}
                  />
                  <Scatter data={aggPts} fill="#00F0FF" fillOpacity={0.75} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-36 flex items-center justify-center border border-white/10 bg-atlas-void/60 text-center px-2">
              <span className="text-[9px] font-mono text-atlas-secondary/75 uppercase tracking-widest">
                Reliability diagram calibrating — awaiting resolved outcomes
              </span>
            </div>
          )}
          <div className="text-[8px] font-mono text-atlas-secondary/75 mt-0.5">
            {diagScope} · predicted → observed · dashed = perfect calibration · dot = #outcomes
          </div>
        </div>
      </div>

      <p className="text-[8px] font-mono text-atlas-secondary/75 mt-1.5 leading-relaxed flex items-start gap-1">
        <Sigma className="w-2.5 h-2.5 mt-px shrink-0" />
        Scores use a Bayesian (Beta-Binomial) calibration curve scored against real-world outcomes — not the AI's raw self-rating.
      </p>
    </div>
  );
};

const REC = {
  CONFIRMED: { icon: CheckCircle2, cls: "text-atlas-red", label: "HIT" },
  EXPIRED: { icon: XCircle, cls: "text-atlas-secondary/80", label: "MISS" },
  PENDING: { icon: CircleDashed, cls: "text-amber-300", label: "PENDING" },
};

// Last 5 locked bulletins for THIS region with their Hit/Miss/Pending outcome.
export const RegionTrackRecord = ({ ledger = [], region, testId }) => {
  const rows = ledger.filter((b) => b.region === region).slice(0, 5);
  if (rows.length === 0) return null;
  return (
    <div className="mt-2 border border-white/10 bg-white/[0.02] p-2.5" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <History className="w-3.5 h-3.5 text-atlas-cyan" />
        <span className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono">
          Track Record · {region} (last {rows.length})
        </span>
      </div>
      <div className="space-y-1">
        {rows.map((b) => {
          const r = REC[b.outcome] || REC.PENDING;
          const Icon = r.icon;
          return (
            <div key={b.uid} className="flex items-center justify-between gap-2 text-[9px] font-mono">
              <span className="text-atlas-secondary/80 shrink-0">{(b.data_cutoff || "").slice(0, 10)}</span>
              <span className="text-atlas-secondary/80 truncate flex-1 min-w-0">{b.name}</span>
              <span className="text-atlas-secondary/75 shrink-0">{Math.round((b.confidence || 0) * 100)}%</span>
              <span className={`inline-flex items-center gap-0.5 shrink-0 ${r.cls}`}>
                <Icon className="w-2.5 h-2.5" /> {r.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
